import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { forkJoin } from 'rxjs';
import { RestService, IS_NOT_EMPTY } from 'cargo-chief-commons';
import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';

interface RestorableMembership {
	projectCarrierMapId: string;
	projectId: string;
	projectName: string;
	selected: boolean;
}

/**
 * Lists the workflows a carrier was removed from by a global exclusion (deleted_reason=EXCLUSION)
 * and lets the user pick which to restore, with a required reason. Returns true on success.
 */
@Component({
	selector: 'app-restore-workflows-dialog',
	templateUrl: './restore-workflows-dialog.component.html',
})
export class RestoreWorkflowsDialogComponent implements OnInit {
	IS_NOT_EMPTY = IS_NOT_EMPTY;

	loading = true;
	saving = false;
	memberships: RestorableMembership[] = [];
	reasonControl: UntypedFormControl;

	constructor(
		public dialogRef: MatDialogRef<RestoreWorkflowsDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		private urlService: UrlService,
		private restService: RestService,
		private utilService: UtilService,
		private cargochief: Cargochief
	) {}

	ngOnInit() {
		this.reasonControl = new UntypedFormControl('', [Validators.required]);
		this.load();
	}

	private load() {
		this.loading = true;
		const membershipsRequest = {
			carrierIds: [this.data.carrierId],
			deleted: true,
			deletedReason: 'EXCLUSION',
		};
		const projectsRequest = {
			accountId: this.cargochief.ACCOUNT_ID,
			page: 0,
			pageSize: 1000,
			assigneeIds: null,
			projectName: null,
			customerName: null,
			dueDate: null,
			sortColumn: 'created',
			sortOrder: 'asc',
			stageIds: null,
		};
		forkJoin([
			this.restService.POST(this.urlService.getProjectCarrierMapsList(), membershipsRequest),
			this.restService.POST(this.urlService.getProjectsList(), projectsRequest),
		]).subscribe({
			next: ([membershipsResponse, projectsResponse]: any[]) => {
				const projectNameById: { [id: string]: string } = {};
				(projectsResponse?.data || []).forEach((project: any) => {
					projectNameById[project.id] = project.name;
				});
				this.memberships = (membershipsResponse?.data || []).map((pcm: any) => ({
					projectCarrierMapId: pcm.id,
					projectId: pcm.projectId,
					projectName: projectNameById[pcm.projectId] || pcm.projectId,
					selected: true,
				}));
				this.loading = false;
			},
			error: () => {
				this.loading = false;
				this.utilService.showSnackbar(
					'Could not load removed workflows.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
			},
		});
	}

	get selectedIds(): string[] {
		return this.memberships.filter((m) => m.selected).map((m) => m.projectCarrierMapId);
	}

	restore() {
		const reason = (this.reasonControl.value || '').trim();
		if (!IS_NOT_EMPTY(reason)) {
			this.reasonControl.markAsTouched();
			this.utilService.showSnackbar('A reason is required.', SNACKBAR.ERROR, SNACKBAR.ACTION);
			return;
		}
		if (!this.selectedIds.length) {
			this.utilService.showSnackbar('Select at least one workflow.', SNACKBAR.ERROR, SNACKBAR.ACTION);
			return;
		}
		this.saving = true;
		const payload = { projectCarrierMapIDs: this.selectedIds, reason };
		this.restService.POST(this.urlService.restoreProjectCarrierMapUrl(), payload).subscribe({
			next: (result: any) => {
				this.utilService.showSnackbar(
					result?.message || 'Carrier restored to workflow(s).',
					SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);
				this.dialogRef.close(true);
			},
			error: () => {
				this.saving = false;
				this.utilService.showSnackbar('Restore failed.', SNACKBAR.ERROR, SNACKBAR.ACTION);
			},
		});
	}

	closeDialog(): void {
		this.dialogRef.close(null);
	}
}
